import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowRight,
  DollarSign,
  Package,
  ShoppingCart,
  TrendingUp,
  Upload,
  Users,
} from 'lucide-react';
import { monthLabel } from '@afiliados/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/StatCard';
import { StatusBadge } from '@/components/StatusBadge';
import { MonthRange } from '@/components/MonthControls';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { dashboardApi } from '@/lib/api/endpoints';
import {
  IMPORT_STATUS_LABEL,
  IMPORT_STATUS_VARIANT,
  fmtDay,
  fmtInt,
  fmtMoney,
  fmtPct,
  fmtRelative,
  fmtUsd,
  fmtUsdCompact,
} from '@/lib/format';

// Paleta validada (dataviz): laranja + ciano passam CVD/contraste em claro e escuro
const COLOR_GROSS = '#ea580c';
const COLOR_NET = '#0891b2';

export function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? undefined;
  const to = params.get('to') ?? undefined;
  const stats = useQuery({
    queryKey: ['dashboard', from, to],
    queryFn: () => dashboardApi.get({ from, to }),
  });
  const s = stats.data;
  const months = s?.availableMonths ?? [];
  const range = s?.range;

  return (
    <div>
      <PageHeader
        title="Painel"
        description="Faturamento dos afiliados consolidado a partir dos relatórios da BuyGoods"
        actions={
          months.length > 0 &&
          range && (
            <div className="flex items-end gap-2">
              <MonthRange
                months={months}
                from={range.from}
                to={range.to}
                onChange={(f, t) => setParams({ from: f, to: t }, { replace: true })}
              />
            </div>
          )
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={DollarSign}
          label="Faturamento bruto"
          value={s ? fmtUsd(s.kpis.grossSales) : undefined}
          hint={s ? `${fmtInt(s.kpis.orders)} pedidos` : undefined}
          loading={stats.isLoading}
        />
        <StatCard
          icon={TrendingUp}
          label="Faturamento líquido"
          value={s ? fmtUsd(s.kpis.netSales) : undefined}
          hint={s ? `comissões ${fmtUsd(s.kpis.commissionsNet)}` : undefined}
          loading={stats.isLoading}
          tone={s && s.kpis.netSales < 0 ? 'destructive' : 'default'}
        />
        <StatCard
          icon={Users}
          label="Afiliados ativos"
          value={s ? fmtInt(s.kpis.affiliatesActive) : undefined}
          hint={s ? `${fmtInt(s.kpis.affiliatesTotal)} cadastrados no período` : undefined}
          loading={stats.isLoading}
        />
        <StatCard
          icon={Package}
          label="Produtos com venda"
          value={s ? fmtInt(s.kpis.productsActive) : undefined}
          hint={s ? `${fmtInt(s.kpis.imports)} relatório(s) ativo(s)` : undefined}
          loading={stats.isLoading}
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Faturamento por mês</CardTitle>
            <CardDescription>Bruto e líquido, somando todos os afiliados</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            {stats.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : s && s.monthly.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={s.monthly} margin={{ left: 0, right: 8, top: 8 }}>
                  <CartesianGrid vertical={false} stroke="var(--border)" />
                  <XAxis
                    dataKey="month"
                    tickFormatter={monthLabel}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v: number) => fmtUsdCompact(v)}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                    axisLine={false}
                    tickLine={false}
                    width={72}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--popover)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--foreground)',
                    }}
                    labelFormatter={(m) => monthLabel(String(m))}
                    formatter={(v, name) => [
                      fmtUsd(Number(v)),
                      name === 'grossSales' ? 'Bruto' : 'Líquido',
                    ]}
                  />
                  <Legend
                    formatter={(v) => (v === 'grossSales' ? 'Bruto' : 'Líquido')}
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="grossSales" fill={COLOR_GROSS} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="netSales" fill={COLOR_NET} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Nenhum relatório importado ainda. Vá em{' '}
                <Link className="ml-1 underline" to="/importacoes">
                  Importações
                </Link>
                .
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Concentração</CardTitle>
            <CardDescription>Curva ABC do faturamento líquido</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : s ? (
              <>
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Top 10 afiliados
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {fmtPct(s.pareto.top10Share, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">do faturamento líquido</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Afiliados que fazem 80%
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {fmtInt(s.pareto.affiliatesTo80)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    de {fmtInt(s.kpis.affiliatesActive)} ativos · {fmtInt(s.kpis.customers)}{' '}
                    clientes · {fmtInt(s.kpis.visitors)} visitantes
                  </p>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Top 10 afiliados</CardTitle>
              <CardDescription>Pelo faturamento líquido no período</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/mensal">
                Ver Planilhão <ArrowRight />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {stats.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : s?.topAffiliates.length ? (
              <ul className="divide-y">
                {s.topAffiliates.map((a, i) => (
                  <li key={a.id}>
                    <Link
                      to={`/afiliados/${a.id}`}
                      className="-mx-2 flex items-center gap-3 rounded px-2 py-2 hover:bg-muted/50"
                    >
                      <span className="w-6 text-right text-xs tabular-nums text-muted-foreground">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{a.name}</p>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max(2, a.share * 100)}%`,
                              background: COLOR_NET,
                            }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium tabular-nums">{fmtMoney(a.netSales)}</p>
                        <p className="text-xs tabular-nums text-muted-foreground">
                          bruto {fmtMoney(a.grossSales)} · {fmtPct(a.share)}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Sem dados no período.</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Produtos</CardTitle>
              <CardDescription>Líquido por produto</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.isLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : s?.topProducts.length ? (
                <ul className="space-y-2">
                  {s.topProducts.slice(0, 6).map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate">{p.name}</span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {fmtMoney(p.netSales)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Sem dados.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="size-4" /> Importações
                </CardTitle>
                <CardDescription>As mais recentes</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/importacoes">
                  <ArrowRight />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {s?.recentImports.length ? (
                <ul className="space-y-2">
                  {s.recentImports.map((i) => (
                    <li key={i.id}>
                      <Link
                        to={`/importacoes/${i.id}`}
                        className="flex items-center justify-between gap-2 text-sm hover:underline"
                      >
                        <span className="truncate">
                          {fmtDay(i.periodStart)}
                          {i.periodStart !== i.periodEnd && ` – ${fmtDay(i.periodEnd)}`}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          {fmtRelative(i.createdAt)}
                          <StatusBadge
                            label={IMPORT_STATUS_LABEL[i.status]}
                            className={IMPORT_STATUS_VARIANT[i.status]}
                          />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma importação ainda.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <p className="mt-6 flex items-center gap-1 text-xs text-muted-foreground">
        <ShoppingCart className="size-3" /> Valores em dólar, como nos relatórios da BuyGoods. Só
        importações ativas entram nas somas.
      </p>
    </div>
  );
}
